import { useFormContext } from 'react-hook-form';
import clsx from 'clsx';
import Checkbox from '@/app/components/Checkbox';
import { PlaceSelect } from '@/app/components/PlaceSelect';
import { Input } from '@/app/components/form/Input';
import { Toggle } from '@/app/components/form/Toggle';
import type { CountrySelectorOption } from '@/hooks/useFormCountryList/useFormCountryList';
import { useCountrySelector } from '@/hooks/useFormCountryList/useFormCountryList';
import { Selector } from '@/app/components/form/Selector';
import type { SingleValue } from 'react-select';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { InfinityAsyncSelector } from '@/app/components/form/Selector/InfinityAsyncSelector';
import { useCallback } from 'react';
import type { StateDataType } from '@/types/common';

interface AddressFieldConfig {
  name: string;
  label: string;
  placeholder: string;
  required?: boolean;
  helperText?: string;
}
type CheckboxConfig = {
  visible: boolean;
  label: string;
  value: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
};

type ToggleConfig = {
  visible: boolean;
  label: string;
  value: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
};
type InputIntent = 'search' | 'secondary' | 'primary' | 'old' | 'primaryWhite' | 'otp' | 'payment';
interface ClientAddressType {
  className?: string;
  fields: {
    address_line1: AddressFieldConfig;
    address_line2: AddressFieldConfig;
    city: AddressFieldConfig;
    state: AddressFieldConfig;
    zip: AddressFieldConfig;
    country: AddressFieldConfig;
  };
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  isDisabled?: boolean;
  checkboxConfig?: CheckboxConfig;
  toggleConfig?: ToggleConfig;
  selectPlaceAddress?: (address: { [key: string]: string }) => void;
  intent?: InputIntent;
}

export const ClientAddress = ({
  onBlur,
  className,
  fields,
  isDisabled,
  checkboxConfig,
  selectPlaceAddress,
  intent,
  toggleConfig,
}: ClientAddressType) => {
  const {
    formState: { errors },
    setValue,
    register,
    watch,
  } = useFormContext();
  const { countryOptions, selectedOption } = useCountrySelector({ selectedId: watch(fields.country.name), url: '/api/worldwide/countries' });

  const getComp = (
    type: string,
    components: google.maps.GeocoderAddressComponent[] | undefined,
    nameType: 'long_name' | 'short_name' = 'long_name'
  ) => components?.find((c) => c.types.includes(type))?.[nameType] || '';

  const handlePlaceSelect = (place: google.maps.places.PlaceResult | null) => {
    const components = place?.address_components;

    const streetNumber = getComp('street_number', components);
    const route = getComp('route', components);
    const subpremise = getComp('subpremise', components);
    const city =
      getComp('locality', components) ||
      getComp('postal_town', components) ||
      getComp('sublocality', components) ||
      getComp('administrative_area_level_2', components);
    const postalCode = getComp('postal_code', components);
    const countryIso2 = getComp('country', components, 'short_name').toLowerCase();
    const countryName = getComp('country', components);
    const countryValue = countryOptions.find((country) => {
      return country.iso2?.toLowerCase() === countryIso2 || country.label.toLowerCase() === countryName.toLowerCase();
    })?.value ?? '';
    const stateValue = getComp('administrative_area_level_1', components, 'short_name');
    const addressPayload: Record<string, string> = {
      [fields.address_line1.name]: [streetNumber, route].filter(Boolean).join(' ').trim(),
      [fields.city.name]: city,
      [fields.zip.name]: postalCode,
    };
    if (countryValue) {
      addressPayload[fields.country.name] = countryValue;
    }
    if (subpremise) {
      addressPayload[fields.address_line2.name] = subpremise;
    }
    if (stateValue) {
      addressPayload[fields.state.name] = stateValue;
    }
    selectPlaceAddress?.(addressPayload);
    setValue(fields.address_line1.name, [streetNumber, route].filter(Boolean).join(' ').trim(), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
    if (countryValue) {
      setValue(fields.country.name, countryValue, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    }
    if (subpremise) {
      setValue(fields.address_line2.name, subpremise, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    }
    if (stateValue) {
      setValue(fields.state.name, stateValue, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    }
    setValue(fields.city.name, city, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    setValue(fields.zip.name, postalCode, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
  };

  const handleInputChange = (value: string) => {
    setValue(fields.address_line1.name, value);
  };

  const handleCountryChange = (value: SingleValue<CountrySelectorOption>) => {
    setValue(fields.country.name, value?.value ?? '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    selectPlaceAddress?.({ [fields.country.name]: value?.value ?? '' });
  };

  const handleStateChange = (value: SelectorOption<string | number> | null) => {
    setValue(fields.state.name, value?.value ?? '', { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    selectPlaceAddress?.({ [fields.state.name]: String(value?.value ?? '') });
  };

  const mapStateOptions = useCallback((options: StateDataType[]) => {
    return options.map((option) => ({ value: option.iso2, label: option.name }));
  }, []);

  const country = watch(fields.country.name) || '';
  const zip = watch(fields.zip.name) || '';
  const state = watch(fields.state.name) || '';
  const city = watch(fields.city.name) || '';
  const address_line1 = watch(fields.address_line1.name) || '';

  const shouldHide = Boolean(toggleConfig?.visible && toggleConfig?.value);

  return (
    <div className={className}>
      {toggleConfig?.visible && (
        <div className={clsx('ml-1', shouldHide ? 'mb-0' : ' mb-4')}>
          <Toggle
            onChange={() => toggleConfig?.onChange(!toggleConfig?.value)}
            checked={toggleConfig.value}
            label={toggleConfig.label}
          />
        </div>
      )}
      {checkboxConfig?.visible && (
        <div className={clsx('ml-1', shouldHide ? 'mb-0' : ' mb-4')}>
          <Checkbox
            label={checkboxConfig.label}
            checked={checkboxConfig.value}
            disabled={checkboxConfig.disabled}
            size="medium"
            variant="transparent"
            onChange={(e) => checkboxConfig?.onChange(e.target.checked)}
          />
        </div>
      )}

      <div
        className={clsx(
          'grid grid-cols-1 md:grid-cols-2 gap-4 transition-all duration-300 ease-in-out',
          shouldHide ? 'max-h-0 opacity-0 overflow-hidden pointer-events-none' : 'max-h-[1000px] opacity-100'
        )}
      >
        <div>
          <PlaceSelect
            autocompleteOptions={{
              fields: ['address_components'],
              types: ['address'],
            }}
            intent={intent}
            value={address_line1}
            autoComplete="off"
            disabled={isDisabled}
            onPlaceSelect={handlePlaceSelect}
            onInputChange={handleInputChange}
            required={fields.address_line1.required}
            label={fields.address_line1.label}
            placeholder={fields.address_line1.placeholder}
            helperText={fields.address_line1.helperText}
            {...register(fields.address_line1.name)}
            errors={errors}
            onBlur={onBlur}
          />
        </div>

        <div>
          <Input
            intent={intent}
            id={fields.address_line2.name}
            label={fields.address_line2.label}
            placeholder={fields.address_line2.placeholder}
            required={fields.address_line2.required}
            helperText={fields.address_line2.helperText}
            {...register(fields.address_line2.name)}
            disabled={isDisabled}
            errors={errors}
            onBlur={onBlur}
          />
        </div>

        {/* Country */}
        <div>
          <Selector
            options={countryOptions}
            placeholder={fields.country.placeholder}
            className="text-left"
            required={fields.country.required}
            errors={errors}
            {...register(fields.country.name)}
            onChange={(newValue: SingleValue<SelectorOption<string>>) => handleCountryChange(newValue)}
            label={fields.country.label}
            disabled={isDisabled}
            value={country}
            menuPosition='fixed'
          />
        </div>

        {/* State/Province */}
        <div>
          <InfinityAsyncSelector
            value={state}
            onChange={handleStateChange}
            label={fields.state.label}
            placeholder={fields.state.placeholder}
            required={fields.state.required}
            errors={errors}
            url={`/api/worldwide/states?iso2=${selectedOption?.iso2 ?? 'us'}`}
            queryKey={[`states-${selectedOption?.iso2 ?? 'us'}`]}
            disabled={isDisabled}
            mapOptions={mapStateOptions}
            menuPosition='fixed'
          />
        </div>

        {/* City */}
        <div>
          <Input
            intent={intent}
            value={city}
            required={fields.city.required}
            label={fields.city.label}
            placeholder={fields.city.placeholder}
            helperText={fields.city.helperText}
            {...register(fields.city.name)}
            errors={errors}
            disabled={isDisabled}
            onBlur={onBlur}
          />
        </div>

        {/* ZIP/Postal Code */}
        <div>
          <Input
            intent={intent}
            value={zip}
            helperText={fields.zip.helperText}
            required={fields.zip.required}
            label={fields.zip.label}
            placeholder={fields.zip.placeholder}
            {...register(fields.zip.name)}
            errors={errors}
            disabled={isDisabled}
            onBlur={onBlur}
          />
        </div>
      </div>
    </div>
  );
};